import { Box, Typography } from "@mui/material";
import { IPaginatedList } from "Interfaces/PaginatedList";
import {
  DataGrid,
  esES,
  GridActionsCellItem,
  GridColDef,
  GridRowParams,
  GridSortModel,
} from "@mui/x-data-grid";
import { useDispatch } from "react-redux";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { useCallback, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal } from "components/Modal";
import { useAppSelector } from "redux/store";
import styles from "scss/Table.module.scss";
import defaultImgActivity from "../../../assets/defaultImgNew.jpg";
import { useIntl } from "react-intl";
import { LanguageContext } from "LanguageProvider";
import {
  IPointOfSaleState,
  setPageIndex,
  setPageSize,
  setSort,
} from "redux/features/pointOfSaleSlice";
import { IPointOfSale } from "Models/PointOfSale";
import { ISelectOption } from "Interfaces/Utils";
import { getMuiLocaleText } from "utils/getMUILocaleText";

interface PointOfSaleTableProps {
  paginatedQuery?: IPaginatedList<IPointOfSale>;
  isLoading: boolean;
  deletePointOfSale: (pointOfSaleId: string) => void;
  companies: ISelectOption<string>[];
  provinces: ISelectOption<string>[];
}

export const Table = ({
  paginatedQuery,
  isLoading,
  deletePointOfSale,
  companies,
  provinces,
}: PointOfSaleTableProps) => {
  const { pageSize, sort, pageIndex } = useAppSelector(
    (state: { pointOfSaleState: IPointOfSaleState }) => state.pointOfSaleState
  );

  const { formatMessage } = useIntl();
  const languageContext = useContext(LanguageContext);
  const locale = languageContext?.locale;
  const localeText = useMemo(() => getMuiLocaleText(locale ?? "es", formatMessage), [locale, formatMessage]);
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);

  const [pointOfSaleIdToDelete, setPointOfSaleIdToDelete] = useState<string | undefined>(
    undefined
  );

  const IMAGE_SERVER = process.env.REACT_APP_IMAGES_ENDPOINT;

  const rows = paginatedQuery?.data ?? [];


  const handleSort = (newSortModel: GridSortModel) => {
    dispatch(setSort(newSortModel));
  };

  const handleChangePage = (newPage: number) => {
    dispatch(setPageIndex(newPage));
  };

  const handleChangeRowsPerPage = (newPageSize: number) => {
    dispatch(setPageSize(newPageSize));
  };

  const onDeleteOpenModal = (pointOfSaleId: string) => {
    setOpenDeleteModal(true);
    setPointOfSaleIdToDelete(pointOfSaleId);
  };

  const onCloseDeleteModal = () => {
    setPointOfSaleIdToDelete(undefined);
    setOpenDeleteModal(false);
  };

  const onAcceptDeleteModal = () => {
    if (pointOfSaleIdToDelete) {
      deletePointOfSale(pointOfSaleIdToDelete);
    }
    setPointOfSaleIdToDelete(undefined);
    setOpenDeleteModal(false);
  };

  const onEdit = useCallback(
    (pointOfSaleId: string) => {
      navigate("/point-of-sale/" + pointOfSaleId);
    },
    [navigate]
  );

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: "image",
        headerName: "",
        width: 80,
        renderCell(params) {
          return (
            <img
              src={
                params.row.image
                  ? `${IMAGE_SERVER}/${params.row.image}`
                  : defaultImgActivity
              }
              alt="Imagen de la pointOfSale"
              style={{
                maxWidth: "100%",
                objectFit: "cover",
                borderRadius: "5px",
              }}
            />
          );
        },
        sortable: false,
      },
      
      {
        field: "name",
        headerName: formatMessage({ id: "pointOfSale.fields.name" }),
        flex: 1,
        renderCell(params) {
          return (
            <Typography
              
              fontSize="14px"
              fontWeight={500}
              color="#111827"
            >
              {params.row.name}
            </Typography>
          );
        },
      },
      {
        field: "company",
        headerName: formatMessage({ id: "pointOfSale.fields.company" }),
        flex: 1,
        renderCell(params) {          
          return (
            <Typography
              sx={{
                color: "#111827",
                fontSize: "14px",
                fontWeight: 500,
                lineHeight: "21px",
                letterSpacing: "0.3px",
              }}
            >
              {companies.find((company) => company.value === params
                .row.companyId)?.label}
            </Typography>
          );
        },
      },
      
      {
        field: "addressType",
        headerName: formatMessage({ id: "pointOfSale.fields.addressType" }),
        flex: 1,
        renderCell(params) {
          return (
            <Typography
              
              fontSize="14px"
              fontWeight={500}
              color="#111827"
            >
              {params.row.addressType}
            </Typography>
          );
        },
      },
      {
        field: "addressName",
        headerName: formatMessage({ id: "pointOfSale.fields.addressName" }),
        flex: 1,
        renderCell(params) {
          return (
            <Typography
              
              fontSize="14px"
              fontWeight={500}
              color="#111827"
            >
              {params.row.addressName}
            </Typography>
          );
        },
      },
      {
        field: "addressNumber",
        headerName: formatMessage({ id: "pointOfSale.fields.addressNumber" }),
        flex: 1,
        renderCell(params) {
          return (
            <Typography
              
              fontSize="14px"
              fontWeight={500}
              color="#111827"
            >
              {params.row.addressNumber}
            </Typography>
          );
        },
      },
      {
        field: "addressDetails",
        headerName: formatMessage({ id: "pointOfSale.fields.addressDetails" }),
        flex: 1,
        renderCell(params) {
          return (
            <Typography
              
              fontSize="14px"
              fontWeight={500}
              color="#111827"
            >
              {params.row.addressDetails}
            </Typography>
          );
        },
      },
      {
        field: "postalCode",
        headerName: formatMessage({ id: "pointOfSale.fields.postalCode" }),
        flex: 1,
        renderCell(params) {
          return (
            <Typography
              
              fontSize="14px"
              fontWeight={500}
              color="#111827"
            >
              {params.row.postalCode}
            </Typography>
          );
        },
      },
      {
        field: "province",
        headerName: formatMessage({ id: "pointOfSale.fields.province" }),
        flex: 1,
        renderCell(params) {
          return (
            <Typography
              
              fontSize="14px"
              fontWeight={500}
              color="#111827"
            > 
              {provinces.find((province) => province.value === params
                .row.province)?.label}
            </Typography>
          );
        }
      },
      {
        field:"locality",
        headerName: formatMessage({ id: "pointOfSale.fields.locality" }),
        flex: 1,
        renderCell(params) {
          return (
            <Typography
              
              fontSize="14px"
              fontWeight={500}
              color="#111827"
            >
              {params.row.locality}
            </Typography>
          );
        },
      },
      {
        field: "action",
        width: 80,
        type: "actions",
        getActions: (params: GridRowParams) => [
          <GridActionsCellItem
            icon={<EditIcon fontSize="small" />}
            onClick={() => onEdit(params.row.id)}
            label="Editar"
            placeholder={"Editar"}
            onPointerEnterCapture={() => {}}
            onPointerLeaveCapture={() => {}}
          />,
          <GridActionsCellItem
            icon={<DeleteIcon fontSize="small" />}
            onClick={() => onDeleteOpenModal(params.row.id)}
            label="Borrar"
            placeholder={"Borrar"}
            onPointerEnterCapture={() => {}}
            onPointerLeaveCapture={() => {}}
          />,
        ],
      },
      
      
    ],
    [IMAGE_SERVER, onEdit, companies, locale]
  );

  return (
    <Box className={styles.DataGridContainer}>
      <Modal.DeleteModal
        open={openDeleteModal}
        title={formatMessage({ id: "pointOfSale.deleteModalMessage" })}
        onAcceptHandler={onAcceptDeleteModal}
        onCancelHandler={onCloseDeleteModal}
      />
      <DataGrid
        rows={rows}
        columns={columns}
        pagination
        pageSize={pageSize}
        page={pageIndex}
        disableSelectionOnClick
        paginationMode="server"
        onPageChange={(newPage) => {
          handleChangePage(newPage);
        }}
        onPageSizeChange={(newPageSize) => handleChangeRowsPerPage(newPageSize)}
        sortModel={sort}
        onSortModelChange={(newSortModel) => handleSort(newSortModel)}
        hideFooter={rows.length === 0}
        autoHeight={rows.length !== 0}
        rowsPerPageOptions={[10, 20, 25]}
        rowCount={paginatedQuery?.total ?? 0}
        localeText={localeText}
        rowHeight={80}
        sx={{
          "& .MuiDataGrid-columnHeaderTitle": {
            color: "#718096",
          },
          "& .MuiDataGrid-columnSeparator": {
            display: 'none',
          },
          "& .MuiDataGrid-columnHeader:hover .MuiDataGrid-menuIcon": {
            display: 'none',
          },
          "& .MuiDataGrid-columnHeadersInner, & .MuiDataGrid-virtualScrollerRenderZone": {
            padding: "0px 0px"
          },
        }}
        loading={isLoading}
      />
      
    </Box>
  );
};
